import { createClient } from '@supabase/supabase-js'
import { inProcessAuthLock } from '@/lib/supabase-auth-lock'

export { inProcessAuthLock } from '@/lib/supabase-auth-lock'

/**
 * Set `VITE_DISABLE_SUPABASE_AUTH=true` when the cloud project is down, CORS/522 blocks
 * `flownotes.localhost`, or you want single-user local without commenting out URL/key.
 * Restart Vite after changing. Clear site localStorage keys `sb-*` if a stale session keeps erroring.
 * Set `FLOWNOTES_LOCAL_SINGLE_USER=true` in the API `.env` so `/api/organizations` and similar work without a Bearer (see .env.example).
 * Optional: `FLOWNOTES_SINGLE_USER_EMAIL` picks which `users` row backs those calls (else oldest `created_at`).
 */
const bypassSupabaseAuth =
  import.meta.env.VITE_DISABLE_SUPABASE_AUTH === 'true' ||
  import.meta.env.VITE_DISABLE_SUPABASE_AUTH === '1'

const supabaseUrl = bypassSupabaseAuth ? '' : import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = bypassSupabaseAuth ? '' : import.meta.env.VITE_SUPABASE_ANON_KEY

/** Last known access token from onAuthStateChange / getSession. Used by fetchWithAuth so boot fetches do not all pile onto getSession(). */
let cachedAccessToken: string | null = null
let cachedAccessTokenExpMs = 0

/** Treat tokens this close to expiry as stale so getSession() refreshes first. */
const ACCESS_TOKEN_EXPIRY_MARGIN_MS = 30_000

function readJwtExpMs(token: string): number {
  try {
    const payload = token.split('.')[1]
    if (!payload) return 0
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = JSON.parse(json)?.exp
    return typeof exp === 'number' ? exp * 1000 : 0
  } catch {
    return 0
  }
}

export function setCachedAccessToken(token: string | null): void {
  cachedAccessToken = token
  cachedAccessTokenExpMs = token ? readJwtExpMs(token) : 0
}

/** Returns null once the token is expired or about to expire (e.g. after laptop sleep, before auto-refresh runs). */
export function getCachedAccessToken(): string | null {
  if (!cachedAccessToken) return null
  if (cachedAccessTokenExpMs && Date.now() > cachedAccessTokenExpMs - ACCESS_TOKEN_EXPIRY_MARGIN_MS) {
    return null
  }
  return cachedAccessToken
}

/** Supabase client for auth (and future realtime/db). Only available when VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set. */
export const supabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          detectSessionInUrl: true,
          persistSession: true,
          autoRefreshToken: true,
          lock: inProcessAuthLock,
        },
      })
    : null

/** True if auth is configured (env vars set). When false, app runs single-user with no login. */
export const isAuthConfigured = Boolean(supabaseUrl && supabaseAnonKey)
